"use client";

import Link from "next/link";
import { MeiliSearchVehicleType } from "../../../types/Vehicle.types";
import Image from "next/image";
import { OdometerIcon } from "../../icons/odometer";
import { Bike, PlugZap } from "lucide-react";
import { FuelIcon } from "../../icons/fuel";
import { TransmissionIcon } from "../../icons/transmission";
import { resetSyncCdnImageSize, stringToSlug } from "../../../utils/common";
import { useTheme } from "next-themes";

export const VehicleCard: React.FC<{ hit: MeiliSearchVehicleType }> = ({
  hit,
}) => {
  const { resolvedTheme } = useTheme();
  const {
    make,
    model,
    version,
    price,
    kilometers,
    fuel,
    transmission,
    id,
    images,
    typeStart,
    style,
    type,
    modelYear,
    category,
    color,
  } = hit;

  const isMotorcycle = type === "Motorcycle";

  const iconColor = resolvedTheme === "dark" ? "#ffffff" : "#000000";

  const detailsVehiclePath = stringToSlug(
    `${make}-${model}-${category ? category : style ? style : ""}-${transmission ?? typeStart}-${fuel}-${modelYear}-${color}:${id}`
  );

  const resizedImages = resetSyncCdnImageSize({
    images,
    width: 350,
    height: 260,
    format: ".avif",
  });

  return (
    <div
      key={id + model + version}
      id={id + model + version}
      className="border rounded-lg overflow-hidden bg-white dark:bg-transparent shadow-sm hover:shadow-lg transition-shadow flex flex-col w-full max-w-[350px]"
    >
      {resizedImages && (
        <Link href={`/veiculo/${detailsVehiclePath}`} className="relative">
          <div className="w-full h-[260px] relative">
            {resizedImages && (
              <Image
                src={resizedImages[0]}
                alt={`${make} ${model}`}
                className="object-cover"
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                priority
              />
            )}
          </div>
        </Link>
      )}
      <div className="pt-[15px] px-[27px] pb-[11px] text-[100%] relative rounded-tl-none rounded-br-[16px] flex-grow">
        <h6 className="font-medium leading-[24px] text-[18px] relative hover:text-[#405FF2]">
          <Link
            href={`/veiculo/${detailsVehiclePath}`}
            className="no-underline cursor-pointer transition-colors duration-300"
          >
            {isMotorcycle ? make : `${make}, ${model}`}

          </Link>
        </h6>
        <div className="text-[14px] truncate">{isMotorcycle ? model : version}</div>
        {/* <div className="text-[14px] truncate min-h-[20px]">{version ?? "\u00A0"}</div> */}

        <div className="border-b-[0.5px] my-5" />
        <ul className="list-inside text-center text-[100%] flex justify-between">
          <li className="text-[14px] flex items-center flex-col">
            <OdometerIcon
              strokeWidth={1.2}
              size={20}
              className="mb-1"
              strokeColor={iconColor}
              fill={iconColor}
            />
            {new Intl.NumberFormat("pt-BR").format(kilometers)} KM
          </li>
          <li className="text-[14px] flex items-center flex-col">
            {isMotorcycle ? (
              <>
                <Bike
                  strokeWidth={1.5}
                  size={20}
                  className="mb-1"
                  color={iconColor}
                />
                {style}
              </>
            ) : (
              <>
                <FuelIcon
                  strokeWidth={1.5}
                  size={20}
                  className="mb-1"
                  strokeColor={iconColor}
                  fill={iconColor}
                />
                {fuel === "Gasolina e elétrico" ? "Híbrido" : fuel}
              </>
            )}
          </li>
          <li className="text-[14px] flex items-center flex-col">
            {isMotorcycle ? (
              <>
                <PlugZap
                  strokeWidth={1.2}
                  size={20}
                  className="mb-1"
                  color={iconColor}
                />
                {typeStart}
              </>
            ) : (
              <>
                <TransmissionIcon
                  strokeWidth={1.2}
                  size={20}
                  className="mb-1"
                  strokeColor={iconColor}
                  fill={iconColor}
                />
                {transmission}
              </>
            )}
          </li>
        </ul>
        <div className="border-b-[0.5px] my-5" />
        <div className="text-[100%] flex items-center justify-between">
          <small className="text-[20px] font-bold leading-[30px]">
            {new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
              maximumFractionDigits: 0,
            }).format(price ?? 0)}
          </small>
          <Link
            href={`/veiculo/${detailsVehiclePath}`}
            className="font-medium cursor-pointer transition-colors duration-300 text-[15px] absolute bottom-[11px] right-[30px] flex items-center justify-center leading-[30px] text-[#405FF2]"
          >
            Ver detalhes
          </Link>
        </div>
      </div>
    </div>
  );
};
